import { FormEvent, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, AlertTriangle, Gauge, RefreshCw, Search, ServerCrash, Timer } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { adminObservabilityApi } from '@/api/adminObservabilityApi'
import { friendlyMessage } from '@/api/http'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatLatency } from '@/lib/utils'

function formatTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat(undefined, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

const formatMs = formatLatency

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(value || 0)
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 }).format(value || 0)
}

function levelVariant(level: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (level === 'error') return 'destructive'
  if (level === 'warn') return 'secondary'
  if (level === 'info') return 'outline'
  return 'default'
}

export default function AdminObservabilityPanel() {
  const { t } = useTranslation()
  const [level, setLevel] = useState('all')
  const [keywordInput, setKeywordInput] = useState('')
  const [keyword, setKeyword] = useState('')

  const logFilters = useMemo(() => ({
    level,
    q: keyword,
    limit: 100,
  }), [keyword, level])

  const logsQuery = useQuery({
    queryKey: ['admin', 'observability', 'logs', logFilters],
    queryFn: () => adminObservabilityApi.logs(logFilters),
  })

  const perfQuery = useQuery({
    queryKey: ['admin', 'observability', 'perf'],
    queryFn: adminObservabilityApi.perfSummary,
  })

  const topModel = perfQuery.data?.models[0]
  const totalCost = (perfQuery.data?.models ?? []).reduce((sum, row) => sum + row.cost_usd, 0)
  const slowest = perfQuery.data?.slow_requests[0]

  const onFilter = (event: FormEvent) => {
    event.preventDefault()
    setKeyword(keywordInput.trim())
  }

  const refreshAll = () => {
    void logsQuery.refetch()
    void perfQuery.refetch()
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <Card className="rounded-lg">
          <CardHeader className="pb-2">
            <CardDescription>{t('adminConsole.observability.metrics.modelP95')}</CardDescription>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Gauge className="size-5 text-muted-foreground" />
              {topModel ? formatMs(topModel.p95_latency_ms) : '-'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{topModel?.model_alias ?? t('adminConsole.observability.empty')}</CardContent>
        </Card>
        <Card className="rounded-lg">
          <CardHeader className="pb-2">
            <CardDescription>{t('adminConsole.observability.metrics.cost')}</CardDescription>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Activity className="size-5 text-muted-foreground" />
              {formatCurrency(totalCost)}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{t('adminConsole.observability.metrics.modelCount', { count: perfQuery.data?.models.length ?? 0 })}</CardContent>
        </Card>
        <Card className="rounded-lg">
          <CardHeader className="pb-2">
            <CardDescription>{t('adminConsole.observability.metrics.slowest')}</CardDescription>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Timer className="size-5 text-muted-foreground" />
              {slowest ? formatMs(slowest.latency_ms) : '-'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{slowest?.model_alias || slowest?.service_kind || slowest?.source || t('adminConsole.observability.empty')}</CardContent>
        </Card>
      </div>

      <Card className="rounded-lg">
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle>{t('adminConsole.observability.perfTitle')}</CardTitle>
              <CardDescription>{t('adminConsole.observability.perfDescription')}</CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={refreshAll}>
              <RefreshCw className="size-4" />
              {t('common.retry')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {perfQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : perfQuery.isError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {friendlyMessage(perfQuery.error, t('common.serviceError'))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminConsole.observability.columns.model')}</TableHead>
                  <TableHead className="text-right">{t('adminConsole.observability.columns.p95')}</TableHead>
                  <TableHead className="text-right">{t('adminConsole.observability.columns.avg')}</TableHead>
                  <TableHead className="text-right">{t('adminConsole.observability.columns.errorRate')}</TableHead>
                  <TableHead className="text-right">{t('adminConsole.observability.columns.cost')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perfQuery.data?.models.length ? perfQuery.data.models.map((row) => (
                  <TableRow key={row.model_alias}>
                    <TableCell className="font-medium">{row.model_alias}</TableCell>
                    <TableCell className="text-right">{formatMs(row.p95_latency_ms)}</TableCell>
                    <TableCell className="text-right">{formatMs(row.avg_latency_ms)}</TableCell>
                    <TableCell className="text-right">{formatPercent(row.error_rate)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.cost_usd)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">{t('adminConsole.observability.empty')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-lg">
        <CardHeader className="gap-4">
          <div>
            <CardTitle>{t('adminConsole.observability.logsTitle')}</CardTitle>
            <CardDescription>{t('adminConsole.observability.logsDescription')}</CardDescription>
          </div>
          <form className="grid gap-3 md:grid-cols-[160px_1fr_auto]" onSubmit={onFilter}>
            <div className="space-y-2">
              <Label>{t('adminConsole.observability.filters.level')}</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('adminConsole.observability.filters.allLevels')}</SelectItem>
                  <SelectItem value="info">INFO</SelectItem>
                  <SelectItem value="warn">WARN</SelectItem>
                  <SelectItem value="error">ERROR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-log-keyword">{t('adminConsole.observability.filters.keyword')}</Label>
              <Input id="admin-log-keyword" value={keywordInput} onChange={(event) => setKeywordInput(event.target.value)} placeholder={t('adminConsole.observability.filters.keywordPlaceholder')} />
            </div>
            <div className="flex items-end">
              <Button type="submit" variant="secondary" className="w-full">
                <Search className="size-4" />
                {t('adminConsole.observability.filters.apply')}
              </Button>
            </div>
          </form>
        </CardHeader>
        <CardContent>
          {logsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : logsQuery.isError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {friendlyMessage(logsQuery.error, t('common.serviceError'))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminConsole.observability.columns.time')}</TableHead>
                  <TableHead>{t('adminConsole.observability.columns.level')}</TableHead>
                  <TableHead>{t('adminConsole.observability.columns.component')}</TableHead>
                  <TableHead>{t('adminConsole.observability.columns.requestId')}</TableHead>
                  <TableHead>{t('adminConsole.observability.columns.message')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logsQuery.data?.entries.length ? logsQuery.data.entries.map((entry, index) => (
                  <TableRow key={`${entry.time}-${entry.request_id ?? index}`}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatTime(entry.time)}</TableCell>
                    <TableCell><Badge variant={levelVariant(entry.level)}>{entry.level.toUpperCase()}</Badge></TableCell>
                    <TableCell>{entry.component || '-'}</TableCell>
                    <TableCell className="font-mono text-xs">{entry.request_id || '-'}</TableCell>
                    <TableCell className="max-w-[520px] truncate">{entry.message}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      <ServerCrash className="mx-auto mb-2 size-5" />
                      {t('adminConsole.observability.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          {logsQuery.data && logsQuery.data.total >= logsQuery.data.limit ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="size-4" />
              {t('adminConsole.observability.logsLimited', { count: logsQuery.data.limit, total: logsQuery.data.total })}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}

